import { useAtom } from 'jotai';
import { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { useIsomorphicLayoutEffect } from '@pairy/react-hooks';

import useUpdateViewerPageState from '../../../../hooks/useUpdateViewerPageState';
import { GOOGLE_EVENT_ID } from '../../../../constant/googleEventId';
import useHandleMessageBoxClick from '../../../../hooks/useHandleMessageBoxClick';
import { currentMessageBoxIdAtom } from '../../../../store/index.atom';
import {
  MessageBoxItemType,
  MessageBoxType,
} from '../../../PdfViewer/index.types';
import useClickOutside from '../../../../hooks/useClickOutside';

import * as Styled from './index.styles';
import MessageBox from './MessageBox';
import {
  MESSAGE_BOX_MAX_WIDTH,
  MESSAGE_BOX_MIN_WIDTH,
  getBoxHeight,
  getBoxWidth,
  getDivideLineIncludeBoxHeight,
  getDivideLineIncludeBoxWidth,
} from './utils';

/* 박스가 화면 가장자리에 닿지 않도록 남기는 여백 */
const EDGE_MARGIN = 8;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * 열린 박스를 배치하는 데 필요한 실측값.
 * 박스 크기와, 페이지 박스가 화면 어디에 있는지(origin), 그리고 화면 크기를 함께 잡는다.
 */
interface MessageBoxLayout {
  boxWidth: number;
  boxHeight: number;
  originX: number;
  originY: number;
  viewportWidth: number;
  viewportHeight: number;
}

const isSameLayout = (a: MessageBoxLayout, b: MessageBoxLayout) =>
  a.boxWidth === b.boxWidth &&
  a.boxHeight === b.boxHeight &&
  a.originX === b.originX &&
  a.originY === b.originY &&
  a.viewportWidth === b.viewportWidth &&
  a.viewportHeight === b.viewportHeight;

export interface Props {
  isPreview?: boolean;
  messageBoxItems?: MessageBoxItemType[];
  resizedImageSize: {
    heightRatio: number;
    widthRatio: number;
    xMargin: number;
    yMargin: number;
    width: number;
    height: number;
  };
  getFormNotSubmitPage?: (page: number) => number | null;
  size?: 'sm' | 'default';
  /** 지정 시 currentPage 대신 해당 페이지의 메세지박스를 렌더한다(V2 스크롤 모드용) */
  page?: number;
}

const MessageBoxLayer = ({
  isPreview = false,
  messageBoxItems,
  resizedImageSize,
  getFormNotSubmitPage,
  size = 'default',
  page,
}: Props) => {
  const [currentMessageBoxId, setCurrentMessageBoxId] = useAtom(
    currentMessageBoxIdAtom,
  );
  const { updateCurrentPage, currentPage } = useUpdateViewerPageState();
  const messageBoxRef = useRef<HTMLElement>(null);
  const dotContainerRef = useRef<HTMLDivElement>(null);
  const oldMessageBoxId = useRef<number | null>(null);

  const [layout, setLayout] = useState<MessageBoxLayout | null>(null);

  const { handleUserMessageBoxClick } = useHandleMessageBoxClick();

  useClickOutside([messageBoxRef], () => {
    oldMessageBoxId.current = currentMessageBoxId;
    setCurrentMessageBoxId(null);
  });

  /* message box는 조회시 전체페이지 값이 오기 때문에 현재 페이지의 메세지박스를 filter해주어야 한다 */
  const currentMessageBoxItems = useMemo(() => {
    if (messageBoxItems) {
      return messageBoxItems?.find((item) => item.page == (page ?? currentPage))
        ?.messageBox;
    }
  }, [messageBoxItems, currentPage, page]);

  /**
   * 박스는 페이지에 얹힌 팝오버라 "화면"에만 들어오면 된다.
   * 슬라이드 폭에 맞춰 좁히면 슬라이드가 작을 때(모바일 축소·가로모드) 박스가
   * 좁고 길쭉해져 오히려 읽기 어려워지므로, 화면 폭을 기준으로 잡고 최소 폭을 보장한다.
   */
  const boxMaxWidth = layout
    ? Math.max(
        MESSAGE_BOX_MIN_WIDTH,
        Math.min(
          MESSAGE_BOX_MAX_WIDTH[size],
          layout.viewportWidth - EDGE_MARGIN * 2,
        ),
      )
    : MESSAGE_BOX_MAX_WIDTH[size];

  const openedMessageBox = currentMessageBoxItems?.find(
    (item) => item.id === currentMessageBoxId,
  );

  /**
   * 열린 박스가 바뀌거나 페이지/화면 크기가 바뀌면 실제 렌더 크기를 다시 잰다.
   * layout effect라 페인트 전에 반영되어 위치가 튀지 않는다.
   */
  useIsomorphicLayoutEffect(() => {
    const boxElement = messageBoxRef.current;
    const containerElement = dotContainerRef.current;

    if (!boxElement || !containerElement) {
      setLayout(null);
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const next: MessageBoxLayout = {
      boxWidth: boxElement.offsetWidth,
      boxHeight: boxElement.offsetHeight,
      originX: containerRect.left,
      originY: containerRect.top,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };

    setLayout((prev) => (prev && isSameLayout(prev, next) ? prev : next));
  }, [
    currentMessageBoxId,
    openedMessageBox?.message,
    resizedImageSize.width,
    resizedImageSize.height,
    boxMaxWidth,
    size,
  ]);

  const getMessageBoxWidth = (text: string) => {
    if (text.includes('\n')) {
      return getDivideLineIncludeBoxWidth(text, size);
    }
    return getBoxWidth(text.length, size);
  };

  const getMessageBoxHeight = (text: string) => {
    if (text.includes('\n')) {
      return getDivideLineIncludeBoxHeight(text, size);
    }
    return getBoxHeight(text.length, size);
  };

  const getMessageBoxPosition = ({
    x,
    y,
    text,
  }: {
    x: number;
    y: number;
    text: string;
  }) => {
    const xLine = resizedImageSize.width / 2;
    const yLine = resizedImageSize.height / 2;

    const dotLeft = x * resizedImageSize.width;
    const dotTop = y * resizedImageSize.height;

    /* 실측값이 있으면 그것을, 첫 프레임에는 추정치를 쓴다 */
    const boxWidth = Math.min(
      layout?.boxWidth ?? getMessageBoxWidth(text),
      boxMaxWidth,
    );
    const boxHeight = layout?.boxHeight ?? getMessageBoxHeight(text);

    /* 어느 방향으로 펼칠지는 dot이 속한 사분면으로 정한다 */
    const isRightHalf = dotLeft > xLine;
    const isBottomHalf = dotTop >= yLine;

    const rawLeft = isRightHalf ? dotLeft - boxWidth : dotLeft + 10;
    const rawTop = isBottomHalf ? dotTop - boxHeight : dotTop + 40;

    if (!layout) {
      return { left: rawLeft, top: rawTop };
    }

    /**
     * 페이지 기준 좌표를 화면 경계로 클램프한다.
     * 슬라이드가 화면보다 작으면 박스가 슬라이드 밖으로 나가더라도 화면 안에는 남고,
     * 슬라이드가 화면보다 크면(확대) 보이는 영역 안으로 들어온다.
     */
    return {
      left: clamp(
        rawLeft,
        EDGE_MARGIN - layout.originX,
        layout.viewportWidth - EDGE_MARGIN - boxWidth - layout.originX,
      ),
      top: clamp(
        rawTop,
        EDGE_MARGIN - layout.originY,
        layout.viewportHeight - EDGE_MARGIN - boxHeight - layout.originY,
      ),
    };
  };

  /*현재 활성화되는 message box로 상태를 업데이트 해준다 */
  const handleDotClick = (messageBoxId: number) => {
    if (messageBoxId === currentMessageBoxId) {
      setCurrentMessageBoxId(null);
    } else {
      /* 새 박스를 열 때는 이전 실측값을 버려 잘못된 위치로 잡히지 않게 한다 */
      setLayout(null);
      setCurrentMessageBoxId(messageBoxId);
    }
  };

  const handleMessageBoxButtonClick = (messageBoxItem: MessageBoxType) => {
    /*메세지 박스 버튼을 클릭했을 경우에는 활성화된 메세지박스가 해제된다(메세지 박스가 닫힌다) */
    setCurrentMessageBoxId(null);

    /*CASE1 특정 페이지로 이동하는 메세지박스일 경우 */
    if (messageBoxItem.type === 'page') {
      if (!isPreview && messageBoxItem.id && handleUserMessageBoxClick) {
        handleUserMessageBoxClick(messageBoxItem.id);
      }

      /*1:1인 경우에는 해당 분기를 진행하지 않는다 */
      if (getFormNotSubmitPage) {
        const notSubmitPage = getFormNotSubmitPage(
          Number(messageBoxItem.reference),
        );
        if (notSubmitPage !== null) {
          updateCurrentPage(notSubmitPage);
          return;
        }
      }

      updateCurrentPage(Number(messageBoxItem.reference));
    } else if (messageBoxItem.type === 'link') {
      window.open(
        messageBoxItem.reference as string,
        '_blank',
        'noreferrer noopener',
      );

      if (!isPreview && messageBoxItem.id) {
        handleUserMessageBoxClick(messageBoxItem.id);
      }
    }
  };

  if (!currentMessageBoxItems || currentMessageBoxItems?.length < 1) {
    return null;
  }

  return (
    <Styled.DotContainer
      ref={dotContainerRef}
      className={cn({
        // NOTE: 확대되면 스크롤이 되어야하므로, 레이어 제한
        // disabledLayer: zoomLevel > 1,
      })}
      style={{
        top: `${resizedImageSize.yMargin}px`,
        left: `${resizedImageSize.xMargin}px`,
        right: `${resizedImageSize.xMargin}px`,
        bottom: `${resizedImageSize.yMargin}px`,
      }}
    >
      {currentMessageBoxItems.map((item) => {
        const boxPosition = getMessageBoxPosition({
          x: item.rect_x,
          y: item.rect_y,
          text: item.message,
        });

        return (
          <div key={item.id}>
            <Styled.Dot
              id={GOOGLE_EVENT_ID.messageBoxDot}
              color={item.color}
              size={size}
              style={{
                position: 'absolute',
                left: `${item.rect_x * resizedImageSize.width}px`,
                top: `${item.rect_y * resizedImageSize.height}px`,
                transform: 'translate(-50%, -50%)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (oldMessageBoxId.current === item.id) {
                  return;
                }
                handleDotClick(item.id);
              }}
            />
            {currentMessageBoxId === item.id && (
              <MessageBox
                ref={messageBoxRef}
                top={`${boxPosition.top}px`}
                left={`${boxPosition.left}px`}
                text={item.message}
                textColor={item.textColor}
                boxColor={item.color}
                buttonText={item.buttonName}
                maxWidth={boxMaxWidth}
                isHidden={layout === null}
                handleButtonClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMessageBoxButtonClick(item);
                }}
                size={size}
              />
            )}
          </div>
        );
      })}
    </Styled.DotContainer>
  );
};

export default MessageBoxLayer;
